import {
  Target,
  TargetAmount,
  TargetTitle,
  TargetType,
  CurrentAmount,
} from './type';

export type TargetTitleValidateError = 'TargetTitleEmpty';

export const targetTitleErrorToString = (
  e: TargetTitleValidateError
): string => {
  switch (e) {
    case 'TargetTitleEmpty':
      return 'タイトルを入力してください';
  }
};

export type TargetAmountValidateError =
  | 'AmountIsMinus'
  | 'AmountIsLow'
  | 'AmountIsHigh'
  | 'AmountIsNull';

export const targetAmountErrorToString = (
  e: TargetAmountValidateError
): string => {
  switch (e) {
    case 'AmountIsMinus':
      return '金額は正の値を入力してください';
    case 'AmountIsLow':
      return '目標額は1万円以上100,000万円以内を入力してください';
    case 'AmountIsHigh':
      return '目標額は1万円以上100,000万円以内の値を入力してください';
    case 'AmountIsNull':
      return '金額を入力してください';
  }
};

export type TargetDateValidateError =
  | 'DateIsNull'
  | 'DateIsBeforeNow'
  | 'DateIsTooLarge';

const ageMaxLimit = 200;

export const targetDateErrorToString = (birthYear: number) => (
  ageOrYear: 'Age' | 'Year'
) => (e: TargetDateValidateError): string => {
  switch (e) {
    case 'DateIsNull':
      return '達成したい時期を入力してください';
    case 'DateIsBeforeNow':
      switch (ageOrYear) {
        case 'Age':
          return '現在の年齢より大きな数字を入力してください';
        case 'Year':
          return '年数には正の数を入力してください';
      }
      break;
    case 'DateIsTooLarge':
      switch (ageOrYear) {
        case 'Age':
          return `${ageMaxLimit}歳以下の年齢を入力してください`;
        case 'Year':
          return `${birthYear +
            ageMaxLimit -
            new Date().getFullYear()}以下の年数を入力してください`;
      }
      break;
  }
};

export type TargetValidateError =
  | {
      tag: 'TitleError';
      error: TargetTitleValidateError;
    }
  | {
      tag: 'DateError';
      error: TargetDateValidateError;
    }
  | { tag: 'AmountError'; error: TargetAmountValidateError };

export const validateTarget = ({
  assetAmount,
  achieved,
  targetAmount,
  targetYear,
  targetTitle,
  targetType,
  thisYear,
  birthYear,
}: {
  targetType: TargetType;
  targetTitle: TargetTitle;
  targetAmount: TargetAmount | null;
  assetAmount: CurrentAmount;
  targetYear: number | null;
  thisYear: number;
  birthYear: number;
  achieved: boolean;
}):
  | {
      tag: 'Success';
      value: Omit<
        Target,
        | 'id'
        | 'startDate'
        | 'accountList'
        | 'excludeAmount'
        | 'reserveAmount'
        | 'yield'
        | 'suggestionLimitPer'
        | 'highRiskPer'
        | 'type'
      >;
    }
  | { tag: 'Error'; errors: TargetValidateError[] } => {
  const targetDate = (():
    | {
        tag: 'DateIsNull' | 'DateMaxLimitNg' | 'DateMinLimitNg';
      }
    | { tag: 'DateIsOk'; value: Date } => {
    if (targetYear === null) return { tag: 'DateIsNull' };
    if (targetYear > birthYear + ageMaxLimit) return { tag: 'DateMaxLimitNg' };
    if (targetYear <= thisYear) return { tag: 'DateMinLimitNg' };
    return { tag: 'DateIsOk', value: new Date(targetYear, 1, 1) };
  })();
  const errors: TargetValidateError[] = [
    !targetTitle
      ? ([
          { tag: 'TitleError', error: 'TargetTitleEmpty' },
        ] as TargetValidateError[])
      : [],
    targetAmount !== null && targetAmount < 0
      ? ([
          { tag: 'AmountError', error: 'AmountIsMinus' },
        ] as TargetValidateError[])
      : [],
    targetAmount !== null && 0 <= targetAmount && targetAmount < 10000
      ? ([
          { tag: 'AmountError', error: 'AmountIsLow' },
        ] as TargetValidateError[])
      : [],
    targetAmount !== null && 1000000000 < targetAmount
      ? ([
          { tag: 'AmountError', error: 'AmountIsHigh' },
        ] as TargetValidateError[])
      : [],
    targetAmount === null
      ? ([
          { tag: 'AmountError', error: 'AmountIsNull' },
        ] as TargetValidateError[])
      : [],
    targetDate.tag === 'DateIsNull'
      ? ([{ tag: 'DateError', error: 'DateIsNull' }] as TargetValidateError[])
      : [],
    targetDate.tag === 'DateMinLimitNg'
      ? ([
          { tag: 'DateError', error: 'DateIsBeforeNow' },
        ] as TargetValidateError[])
      : [],
    targetDate.tag === 'DateMaxLimitNg'
      ? ([
          { tag: 'DateError', error: 'DateIsTooLarge' },
        ] as TargetValidateError[])
      : [],
  ].flat();
  if (errors.length || targetAmount === null || targetDate.tag !== 'DateIsOk')
    return { tag: 'Error', errors };
  return {
    tag: 'Success',
    value: {
      refType: targetType,
      title: targetTitle,
      targetAmount,
      assetAmount,
      endDate: targetDate.value.toString(),
      achieved,
    },
  };
};
